import {
  integrationDataSourcesSchema,
  type IntegrationDataSource,
} from "@proof/core";
import { IntegrationDataSources } from "./IntegrationDataSources";
import { useEffect, useState } from "react";
import type { IntegrationCredentialsService } from "@proof/accounts";
import { accountRequest, AccountRequestError } from "./account-api";
import { Button, Control, Modal } from "./components";
import { useWorkspaceTransport } from "./WorkspaceContext";
type View = Awaited<ReturnType<IntegrationCredentialsService["list"]>>;
type Client = View["clients"][number];
const labels: Record<string, string> = {
  "tenant.integration-data.use": "Read granted external form data",
  "tenant.documents.view": "View projects",
  "tenant.documents.manage": "Create and edit projects",
  "tenant.templates.publish": "Publish projects",
  "tenant.proofs.use": "Personalize and submit proofs",
  "tenant.proofs.generate": "Generate output",
  "tenant.assets.use": "Read assets and fonts",
  "tenant.assets.manage": "Upload and manage assets",
  "tenant.resources.manage": "Manage reusable resources",
  "tenant.fonts.manage": "Manage fonts",
  "tenant.renders.operate": "Operate workspace render jobs",
};
export function IntegrationAccess({
  notify,
}: {
  notify: (message: string) => void;
}) {
  const transport = useWorkspaceTransport();
  const [view, setView] = useState<View>(),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [dataSources, setDataSources] = useState<IntegrationDataSource[]>([]);
  const [sourceCredential, setSourceCredential] = useState({
    clientId: "",
    sourceId: "",
    secret: "",
  });
  const [name, setName] = useState(""),
    [minutes, setMinutes] = useState(15),
    [origins, setOrigins] = useState("");
  const [scopes, setScopes] = useState<string[]>([
    "tenant.integration-data.use",
    "tenant.documents.view",
    "tenant.proofs.use",
    "tenant.proofs.generate",
    "tenant.assets.use",
  ]);
  const [editing, setEditing] = useState<Client>(),
    [secret, setSecret] = useState<{ client: Client; client_secret: string }>();
  const [pending, setPending] = useState<{
      client: Client;
      action: "rotate" | "revoke";
    }>(),
    [password, setPassword] = useState("");
  const load = async () => setView(await accountRequest<View>("/integrations"));
  useEffect(() => {
    if (!transport.demo) void load().catch((e) => setError(e.message));
  }, [transport.demo]);
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await work();
      await load();
    } catch (e) {
      setError(
        e instanceof AccountRequestError && e.code === "ACCOUNT_INPUT_INVALID"
          ? "Check the client name, permissions, lifetime, and exact website origins."
          : e instanceof Error
            ? e.message
            : "The request failed",
      );
    } finally {
      setBusy(false);
    }
  };
  const reset = () => {
    setEditing(undefined);
    setName("");
    setDataSources([]);
    setMinutes(15);
    setOrigins("");
  };
  const endpoint = location.origin + "/api/oauth/token";
  if (transport.demo)
    return (
      <section className="panel padded">
        <h2>Integration access</h2>
        <p>
          B2B clients are available when Proof Studio uses application accounts.
          Sign in to an application workspace to manage credentials.
        </p>
      </section>
    );
  return (
    <div className="integration-access">
      <section className="panel padded">
        <h2>Integration access</h2>
        <p>
          Create server-side credentials for this workspace. Your backend
          exchanges them for short-lived tokens with a subset of the permissions
          you allow. Tokens authorize the integration itself, rather than a
          signed-in person.
        </p>
        <p>
          Client secrets stay on your server. Only a narrowly scoped access
          token goes to an embedded browser component.
        </p>
        {error && <p role="alert">{error}</p>}
        {!view && !error && <p role="status">Loading integration clients…</p>}
        {error && (
          <Button disabled={busy} onClick={() => void run(load)}>
            Refresh
          </Button>
        )}
      </section>
      {view && (
        <>
          <section className="panel padded">
            <h3>
              {editing
                ? "Edit integration client"
                : "Create integration client"}
            </h3>
            <p>
              Project permissions apply across this workspace. External data has
              separate source, field, and record grants. Updating a client,
              rotating its secret, or revoking it invalidates all of its
              existing tokens.
            </p>
            <div className="control-grid">
              <Control label="Client name">
                <input
                  value={name}
                  maxLength={100}
                  onChange={(e) => setName(e.target.value)}
                />
              </Control>
              <Control label="Maximum token lifetime (minutes)">
                <input
                  type="number"
                  min={1}
                  max={1440}
                  value={minutes}
                  onChange={(e) => setMinutes(Number(e.target.value))}
                />
              </Control>
            </div>
            <Control label="Allowed embed origins (one per line)">
              <textarea
                value={origins}
                placeholder="https://your-business.example"
                onChange={(e) => setOrigins(e.target.value)}
              />
            </Control>
            <p className="muted">
              Use exact HTTPS origins without paths. Leave empty for
              server-to-server access only. Localhost HTTP is supported for
              development.
            </p>
            <IntegrationDataSources
              value={dataSources}
              onChange={setDataSources}
            />
            <fieldset>
              <legend>Maximum permissions</legend>
              <div className="integration-permissions">
                {view.capabilities.map((scope) => (
                  <label key={scope}>
                    <input
                      type="checkbox"
                      checked={scopes.includes(scope)}
                      onChange={(e) =>
                        setScopes((current) =>
                          e.target.checked
                            ? [...current, scope]
                            : current.filter((s) => s !== scope),
                        )
                      }
                    />
                    {labels[scope]}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="button-row">
              <Button
                kind="primary"
                disabled={
                  busy ||
                  !name.trim() ||
                  !scopes.length ||
                  !Number.isFinite(minutes) ||
                  minutes < 1 ||
                  minutes > 1440
                }
                onClick={() =>
                  void run(async () => {
                    const parsedSources =
                      integrationDataSourcesSchema.safeParse(
                        dataSources.map((source) => ({
                          ...source,
                          parameterKeys: source.parameterKeys
                            .map((key) => key.trim())
                            .filter(Boolean),
                          recordIds:
                            source.recordIds === null
                              ? null
                              : source.recordIds
                                  .map((id) => id.trim())
                                  .filter(Boolean),
                        })),
                      );
                    if (!parsedSources.success)
                      throw new Error(
                        "Check source IDs, field mappings, record limits, and fixed HTTPS endpoints.",
                      );
                    const configuration = {
                      dataSources: parsedSources.data,
                      name,
                      scopes,
                      maxTokenSeconds: minutes * 60,
                      allowedOrigins: origins
                        .split(/\r?\n/)
                        .map((o) => o.trim())
                        .filter(Boolean),
                    };
                    if (editing) {
                      await accountRequest("/integrations/clients/change", {
                        id: editing.id,
                        expectedRevision: editing.revision,
                        action: "update",
                        configuration,
                      });
                      notify("Client updated; previous tokens revoked");
                    } else {
                      const result = await accountRequest<{
                        client: Client;
                        client_secret: string;
                      }>("/integrations/clients", configuration);
                      setSecret(result);
                      notify("Integration client created");
                    }
                    reset();
                  })
                }
              >
                {editing ? "Save client" : "Create client"}
              </Button>
              {editing && (
                <Button disabled={busy} onClick={reset}>
                  Cancel editing
                </Button>
              )}
            </div>
            <details>
              <summary>Confirm password for sensitive changes</summary>
              <p>
                Creating, changing, or rotating credentials requires a password
                confirmation within the last ten minutes.
              </p>
              <Control label="Current password">
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Control>
              <Button
                disabled={busy || !password}
                onClick={() =>
                  void run(async () => {
                    await accountRequest("/reauthenticate", { password });
                    setPassword("");
                    notify("Password confirmed; retry your change");
                  })
                }
              >
                Confirm password
              </Button>
            </details>
          </section>
          <section className="panel padded">
            <h3>Workspace clients</h3>
            {!view.clients.length && <p>No integration clients yet.</p>}
            <div className="integration-client-list">
              {view.clients.map((client) => (
                <article key={client.id} className="panel padded">
                  <h4>
                    {client.name} · {client.active ? "Active" : "Revoked"}
                  </h4>
                  <p className="integration-client-id">
                    Client ID: <code>{client.id}</code>
                  </p>
                  <p>Maximum lifetime: {client.maxTokenSeconds / 60} minutes</p>
                  <p>{client.scopes.map((s) => labels[s]).join(" · ")}</p>
                  <p>
                    Embed origins:{" "}
                    {client.allowedOrigins.join(", ") ||
                      "Server-to-server only"}
                  </p>
                  {client.dataSources.map((source) => (
                    <div key={source.id}>
                      <p>
                        <strong>{source.name}</strong> �{" "}
                        <code>{source.id}</code> �{" "}
                        {source.mode === "host"
                          ? "Backend-supplied data"
                          : "External HTTPS API"}
                      </p>
                      <p>
                        Fields:{" "}
                        {source.fields.map((field) => field.key).join(", ")} �
                        Records:{" "}
                        {source.recordIds?.join(", ") ||
                          "Chosen by backend for each token"}
                      </p>
                      {client.active && source.mode === "http" && (
                        <details>
                          <summary>Set or replace source credential</summary>
                          <p>
                            Enter the complete header value, such as Bearer
                            followed by your API token. The value is encrypted
                            server-side and never returned. Saving revokes
                            existing form tokens.
                          </p>
                          <Control label="Source credential">
                            <input
                              type="password"
                              autoComplete="new-password"
                              value={
                                sourceCredential.clientId === client.id &&
                                sourceCredential.sourceId === source.id
                                  ? sourceCredential.secret
                                  : ""
                              }
                              onChange={(e) =>
                                setSourceCredential({
                                  clientId: client.id,
                                  sourceId: source.id,
                                  secret: e.target.value,
                                })
                              }
                            />
                          </Control>
                          <Button
                            disabled={
                              busy ||
                              sourceCredential.clientId !== client.id ||
                              sourceCredential.sourceId !== source.id
                            }
                            onClick={() =>
                              void run(async () => {
                                await accountRequest(
                                  "/integrations/data-source-secret",
                                  {
                                    ...sourceCredential,
                                    expectedRevision: client.revision,
                                  },
                                );
                                setSourceCredential({
                                  clientId: "",
                                  sourceId: "",
                                  secret: "",
                                });
                                notify(
                                  "Source credential saved; old tokens revoked",
                                );
                              })
                            }
                          >
                            Save source credential
                          </Button>
                        </details>
                      )}
                    </div>
                  ))}
                  {client.active && (
                    <div className="button-row">
                      <Button
                        disabled={busy}
                        onClick={() => {
                          setEditing(client);
                          setDataSources(client.dataSources);
                          setName(client.name);
                          setMinutes(client.maxTokenSeconds / 60);
                          setScopes(client.scopes);
                          setOrigins(client.allowedOrigins.join("\n"));
                        }}
                      >
                        Edit permissions
                      </Button>
                      <Button
                        disabled={busy}
                        onClick={() => setPending({ client, action: "rotate" })}
                      >
                        Rotate secret
                      </Button>
                      <Button
                        disabled={busy}
                        onClick={() => setPending({ client, action: "revoke" })}
                      >
                        Revoke client
                      </Button>
                    </div>
                  )}
                  {view.tokens
                    .filter((t) => t.clientId === client.id)
                    .slice(-50)
                    .reverse()
                    .map((t) => (
                      <div key={t.id} className="integration-token-row">
                        <span>
                          <code>{t.id}</code> ·{" "}
                          {t.revoked
                            ? "Revoked"
                            : t.expiresAt <= Date.now()
                              ? "Expired"
                              : `Expires ${new Date(t.expiresAt).toLocaleString()}`}
                        </span>
                        {!t.revoked && t.expiresAt > Date.now() && (
                          <Button
                            disabled={busy}
                            onClick={() =>
                              void run(async () => {
                                await accountRequest(
                                  "/integrations/tokens/revoke",
                                  { id: t.id },
                                );
                                notify("Access token revoked");
                              })
                            }
                          >
                            Revoke token
                          </Button>
                        )}
                      </div>
                    ))}
                </article>
              ))}
            </div>
          </section>
          <section className="panel padded">
            <h3>Connect your backend</h3>
            <p>
              <a
                href="/api/accounts/integrations/openapi.json"
                target="_blank"
                rel="noreferrer"
              >
                Integration API contract (OpenAPI)
              </a>
            </p>
            <p>
              Token endpoint: <code>{endpoint}</code>. No refresh tokens are
              issued. Your backend repeats the exchange when the token expires.
              Keep your client ID and secret in your server's secret store.
            </p>
            <pre>{`const response = await fetch('${endpoint}', {
  method: 'POST',
  headers: {
    Authorization: 'Basic ' + Buffer.from(
      process.env.PROOF_CLIENT_ID + ':' + process.env.PROOF_CLIENT_SECRET
    ).toString('base64'),
    'Content-Type': 'application/x-www-form-urlencoded'
  },
  body: new URLSearchParams({
    grant_type: 'client_credentials',
    scope: 'tenant.documents.view tenant.proofs.use tenant.proofs.generate tenant.assets.use',
    expires_in: '300'
  })
});
if (!response.ok) throw new Error('Token exchange failed');
const { access_token, expires_in } = await response.json();`}</pre>
            <p>
              Your own token-delivery endpoint must authenticate your customer
              and choose the permissions it needs. Keep browser tokens in
              memory, and supply them through the component's{" "}
              <code>headers</code> property before connecting it.
            </p>
          </section>
          <section className="panel padded">
            <h3>Recent access activity</h3>
            {view.audit
              .slice()
              .reverse()
              .map((e) => (
                <p key={e.id}>
                  {new Date(e.at).toLocaleString()} ·{" "}
                  {e.action.replaceAll("integration.", "").replaceAll(".", " ")}{" "}
                  · <code>{e.target}</code>
                </p>
              ))}
          </section>
        </>
      )}
      {secret && (
        <Modal
          title="Save your client secret"
          onClose={() => setSecret(undefined)}
        >
          <p>
            This secret is shown once. Save it in your backend's secret store.
            Closing this dialog removes it from this page.
          </p>
          <p>
            Client ID: <code>{secret.client.id}</code>
          </p>
          <pre className="integration-secret">{secret.client_secret}</pre>
          <div className="button-row">
            <Button
              onClick={() =>
                void navigator.clipboard.writeText(secret.client_secret).then(
                  () => notify("Secret copied"),
                  () =>
                    setError(
                      "Clipboard unavailable; copy the secret from this dialog",
                    ),
                )
              }
            >
              Copy secret
            </Button>
            <Button kind="primary" onClick={() => setSecret(undefined)}>
              I saved the secret
            </Button>
          </div>
        </Modal>
      )}
      {pending && (
        <Modal
          title={
            pending.action === "rotate"
              ? "Rotate client secret?"
              : "Revoke integration client?"
          }
          onClose={() => {
            if (!busy) setPending(undefined);
          }}
        >
          <p>
            This immediately invalidates all tokens issued to{" "}
            {pending.client.name}.
            {pending.action === "rotate"
              ? " Its previous secret will stop working."
              : " It will no longer be able to request tokens."}
          </p>
          <div className="button-row">
            <Button disabled={busy} onClick={() => setPending(undefined)}>
              Cancel
            </Button>
            <Button
              kind="primary"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const result = await accountRequest<{
                    client: Client;
                    client_secret?: string;
                  }>("/integrations/clients/change", {
                    id: pending.client.id,
                    expectedRevision: pending.client.revision,
                    action: pending.action,
                  });
                  if (result.client_secret)
                    setSecret({
                      client: result.client,
                      client_secret: result.client_secret,
                    });
                  setPending(undefined);
                  notify("Integration credentials updated");
                })
              }
            >
              Confirm {pending.action === "rotate" ? "rotation" : "revocation"}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
